// src/services/works/additionalImageService.js

import store from '@/store';
import imageService from '../imageService';
import { setStepCount } from './workProgress';
import { dimensionsText } from './detailImageMin';

/**
 * 추가 이미지 처리 서비스
 */
const additionalImageService = {
  /**
   * 추가 이미지 처리 메인 함수
   * @param {Object} product - 상품 객체
   * @returns {Promise<Object>} 처리 결과
   */
  async process(product) {
    console.log('추가 이미지 처리 시작:', product);

    try {
      // 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '추가 이미지 처리 중...'
      });

      // 메인 이미지 가져오기
      const mainImages = product.mainImages || [];

      // 첫 번째 옵션의 대표 이미지 가져오기 (이미 처리된 이미지)
      let usedMainImage = null;
      if (product.options && product.options.length > 0 &&
        product.options[0].mainImageStatus &&
        product.options[0].mainImageStatus.image) {
        usedMainImage = product.options[0].mainImageStatus.image;
        console.log('대표 이미지로 사용된 이미지 URL:', usedMainImage);
      }

      // 대표 이미지를 제외한 메인 이미지들 필터링
      let additionalImages = mainImages.filter(img => img !== usedMainImage);
      console.log(`처리할 추가 이미지 개수: ${additionalImages.length}`);

      // 최대 9개까지만 처리 (쿠팡 추가 이미지 제한)
      additionalImages = additionalImages.slice(0, 9);

      // 결과를 저장할 배열
      const processedImages = [];

      // 병렬 처리를 위한 동시 실행 개수 제한
      const CONCURRENT_LIMIT = 3;

      // 단일 이미지 처리 함수 (병렬 실행용)
      const processSingleImage = async (imageUrl, index) => {
        try {
          console.log(`추가 이미지 ${index + 1} 처리 시작:`, imageUrl.substring(0, 50) + '...');

          // 이미지 텍스트 제거
          const result = await imageService.removeTextFromImage(imageUrl);

          console.log(`추가 이미지 ${index + 1} removeTextFromImage 결과:`, result);

          // 텍스트 제거 결과 추출 (여러 가능한 형식 처리)
          let processedImageUrl = null;

          // 1. 객체이고 url 속성이 있는 경우 (기존 응답 형식)
          if (result && result.url) {
            processedImageUrl = result.url;
          }
          // 2. 문자열인 경우 (URL 직접 반환)
          else if (typeof result === 'string') {
            processedImageUrl = result;
          }
          // 3. requestId와 processedUrl이 있는 경우 (새로운 응답 형식)
          else if (result && result.processedUrl) {
            processedImageUrl = result.processedUrl;
          }

          console.log(`추가 이미지 ${index + 1} 추출된 처리된 이미지 URL:`, processedImageUrl);

          if (processedImageUrl) {
            // 이미지 해상도 확인 및 필요시 리사이징
            const resizeResult = await this.resizeImageIfNeeded(processedImageUrl);
            const { imageData: base64Data, dimensions } = resizeResult;

            // 파일명 생성
            const fileName = `${product.registrationId}_additional_${index + 1}_${Date.now()}.jpg`;

            // S3에 업로드
            const uploadResult = await imageService.uploadImageToS3(base64Data, fileName, 'additional');

            // 업로드 결과 처리 (객체 또는 문자열)
            let uploadedUrl;
            if (uploadResult && typeof uploadResult === 'object') {
              if (uploadResult.imageUrl) {
                uploadedUrl = uploadResult.imageUrl;
              } else {
                console.error(`추가 이미지 ${index + 1} 업로드 결과에 imageUrl이 없습니다:`, uploadResult);
                return null;
              }
            } else if (typeof uploadResult === 'string') {
              uploadedUrl = uploadResult;
            } else {
              console.error(`추가 이미지 ${index + 1} 알 수 없는 업로드 결과 형식:`, uploadResult);
              return null;
            }

            if (uploadedUrl) {
              console.log(`추가 이미지 ${index + 1} 처리 완료:`, uploadedUrl);
              return {
                index: index,
                originalUrl: imageUrl,
                url: uploadedUrl,
                dimensions: `${dimensions.width}x${dimensions.height}`,
                filesize: imageService.estimateFileSize(base64Data),
                textRemoved: true
              };
            }
          }
          return null;
        } catch (error) {
          console.error(`이미지 ${index + 1} 처리 중 오류:`, error);
          return null;
        }
      };

      // 병렬 처리 (동시 CONCURRENT_LIMIT개씩)
      console.log(`추가 이미지 ${additionalImages.length}개 병렬 처리 시작 (동시 ${CONCURRENT_LIMIT}개)`);

      for (let i = 0; i < additionalImages.length; i += CONCURRENT_LIMIT) {
        const batch = additionalImages.slice(i, i + CONCURRENT_LIMIT);
        const batchStartIndex = i;

        // 진행 상황 업데이트
        store.dispatch('setProcessing', {
          isProcessing: true,
          message: `추가 이미지 처리 중... (${Math.min(i + CONCURRENT_LIMIT, additionalImages.length)}/${additionalImages.length})`
        });

        // 배치 병렬 처리
        const batchResults = await Promise.allSettled(
          batch.map((imageUrl, batchIndex) =>
            processSingleImage(imageUrl, batchStartIndex + batchIndex)
          )
        );

        // 성공한 결과만 수집
        batchResults.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            processedImages.push(result.value);
          }
        });
        setStepCount(product.registrationId, 'additionalImage', Math.min(i + CONCURRENT_LIMIT, additionalImages.length), additionalImages.length);
      }

      // 인덱스 순으로 정렬
      processedImages.sort((a, b) => a.index - b.index);

      console.log(`추가 이미지 병렬 처리 완료: ${processedImages.length}개 성공`);

      // 처리된 이미지 URL 추출
      const processedImageUrls = processedImages.map(img => img.url).filter(Boolean);
      const additionalImagesJoined = processedImageUrls.join(',');

      // 부분 업데이트를 위한 필드 지정
      const updateFields = {
        'additionalImageStatus': 'completed',
        'additionalImages': processedImages,
        'updatedAt': Date.now()
      };

      // 상품 레벨의 coupangDoc 업데이트 (있는 경우)
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        // 모든 jsonDocument 항목에 이미지 정보 적용
        product.coupangDoc.jsonDocument.forEach((docItem, docIndex) => {
          if (docItem.imagePage) {
            // 이미지 페이지가 있는 경우
            if (!docItem.imagePage.images) {
              // 메모리 상에서 객체 생성
              docItem.imagePage.images = {};
              // 점 표기법으로 빈 객체 설정
              updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images`] = {};
            }

            // 추가 이미지 URL 업데이트 (점 표기법 사용)
            updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.additionalImage`] = additionalImagesJoined;
          }
        });
      }

      // 옵션 정보 업데이트
      if (product.options && product.options.length > 0) {
        product.options.forEach((option, optionIndex) => {
          // 옵션의 상태 업데이트
          updateFields[`options.${optionIndex}.additionalImageStatus.status`] = 'completed';
        });
      }

      // 서버에 부분 업데이트 요청
      await store.dispatch('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      // 결과 반환
      return {
        status: processedImages.length > 0 ? 'completed' : 'error',
        type: 'additionalImage',
        additionalImages: processedImages,
        count: processedImages.length
      };
    } catch (error) {
      console.error('추가 이미지 처리 중 오류:', error);
      return {
        status: 'error',
        type: 'additionalImage',
        error: error.message
      };
    } finally {
      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });
    }
  },

  /**
   * 단일 이미지 처리
   * @param {string} imageUrl - 처리할 이미지 URL
   * @param {string} productId - 상품 등록번호
   * @param {number} index - 이미지 인덱스
   * @returns {Promise<Object>} 처리된 이미지 정보
   */
  async processImage(imageUrl, productId, index) {
    try {
      // 이미지 텍스트 제거
      const result = await imageService.removeTextFromImage(imageUrl);
      console.log('processImage - removeTextFromImage 결과:', result);

      // 텍스트 제거 결과 추출 (여러 가능한 형식 처리)
      let processedImageUrl = null;

      // 1. 객체이고 url 속성이 있는 경우 (기존 응답 형식)
      if (result && result.url) {
        processedImageUrl = result.url;
      }
      // 2. 문자열인 경우 (URL 직접 반환)
      else if (typeof result === 'string') {
        processedImageUrl = result;
      }
      // 3. requestId와 processedUrl이 있는 경우 (새로운 응답 형식)
      else if (result && result.processedUrl) {
        processedImageUrl = result.processedUrl;
      }

      console.log('processImage - 추출된 처리된 이미지 URL:', processedImageUrl);

      if (processedImageUrl) {
        // 이미지 해상도 확인 및 필요시 리사이징
        const resizeResult = await this.resizeImageIfNeeded(processedImageUrl);
        const { imageData: base64Data, dimensions } = resizeResult;

        // 파일명 생성
        const fileName = `${productId}_additional_${index}_${Date.now()}.jpg`;

        // S3에 업로드
        const uploadResult = await imageService.uploadImageToS3(base64Data, fileName, 'additional');

        // 업로드 결과 처리 (객체 또는 문자열)
        let uploadedUrl;
        if (uploadResult && typeof uploadResult === 'object') {
          // 객체 형태로 반환된 경우 (신규 형식)
          if (uploadResult.imageUrl) {
            uploadedUrl = uploadResult.imageUrl;
          } else {
            console.error(`이미지 업로드 결과에 imageUrl이 없습니다:`, uploadResult);
            return null;
          }
        } else if (typeof uploadResult === 'string') {
          // 문자열로 반환된 경우 (이전 형식)
          uploadedUrl = uploadResult;
        } else {
          console.error(`알 수 없는 업로드 결과 형식:`, uploadResult);
          return null;
        }

        if (uploadedUrl) {
          // 처리된 이미지 정보 반환
          return {
            originalUrl: imageUrl,
            url: uploadedUrl,
            dimensions: `${dimensions.width}x${dimensions.height}`,
            filesize: imageService.estimateFileSize(base64Data),
            textRemoved: true
          };
        }
      }

      return null;
    } catch (error) {
      console.error('이미지 처리 중 오류:', error);
      return null;
    }
  },

  /**
   * 파일 업로드 처리
   * @param {File[]} files - 업로드할 파일 배열
   * @param {string} productId - 상품 등록번호
   * @returns {Promise<Array>} 처리된 이미지 정보 배열
   */
  async uploadFiles(files, productId) {
    const processedImages = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const file = files[i];

        // 파일을 Base64로 변환
        const originalBase64Data = await imageService.fileToBase64(file);

        // 임시 URL 생성하여 리사이징 확인
        const tempUrl = URL.createObjectURL(file);

        // 이미지 해상도 확인 및 필요시 리사이징
        let resizeResult;
        try {
          resizeResult = await this.resizeImageIfNeeded(tempUrl);
        } catch (resizeError) {
          console.warn(`파일 ${i + 1} 리사이징 실패, 원본 사용:`, resizeError);
          // 리사이징 실패 시 원본 사용
          const dimensions = await this.getImageDimensions(tempUrl);
          resizeResult = {
            imageData: originalBase64Data,
            dimensions: dimensions
          };
        } finally {
          // 임시 URL 해제
          URL.revokeObjectURL(tempUrl);
        }

        const { imageData: base64Data, dimensions } = resizeResult;

        // 파일명 생성
        const fileName = `${productId}_additional_upload_${i}_${Date.now()}.jpg`;

        // S3에 업로드
        const uploadResult = await imageService.uploadImageToS3(base64Data, fileName, 'additional');

        // 업로드 결과 처리 (객체 또는 문자열)
        let uploadedUrl;
        if (uploadResult && typeof uploadResult === 'object') {
          // 객체 형태로 반환된 경우 (신규 형식)
          if (uploadResult.imageUrl) {
            uploadedUrl = uploadResult.imageUrl;
          } else {
            console.error(`파일 ${i + 1} 업로드 결과에 imageUrl이 없습니다:`, uploadResult);
            continue; // 다음 파일로 넘어감
          }
        } else if (typeof uploadResult === 'string') {
          // 문자열로 반환된 경우 (이전 형식)
          uploadedUrl = uploadResult;
        } else {
          console.error(`파일 ${i + 1} 알 수 없는 업로드 결과 형식:`, uploadResult);
          continue; // 다음 파일로 넘어감
        }

        if (uploadedUrl) {
          // 처리된 이미지 정보 저장
          processedImages.push({
            originalUrl: null,
            url: uploadedUrl,
            dimensions: dimensionsText(dimensions), // 리사이즈 실패 길에서 못 잼(null)이면 빈 칸
            filesize: imageService.estimateFileSize(base64Data),
            textRemoved: true  // 업로드된 이미지도 추가 이미지로 사용 가능하도록 true로 설정
          });
        }
      } catch (error) {
        console.error(`파일 ${i + 1} 업로드 중 오류:`, error);
      }
    }

    return processedImages;
  },

  /**
   * 이미지 크기 가져오기
   * @param {string} imageUrl - 이미지 URL
   * @returns {Promise<{width: number, height: number}|null>} - 이미지 크기(못 잼 = null)
   */
  getImageDimensions(imageUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = (err) => {
        // 못 잼 = null(기본 크기 지어내기 0) — 기록 크기 칸은 비고, 보내기 전 크기 문(rocket-submit)이 다시 잰다
        console.error('추가이미지 크기 못 잼:', imageUrl, err);
        resolve(null);
      };
      img.src = imageUrl;
    });
  },

  /**
   * 쿠팡 해상도 범위 확인 및 리사이징
   * @param {string} imageUrl - 이미지 URL
   * @returns {Promise<{imageData: string, dimensions: {width: number, height: number}}>} - 리사이징된 이미지 데이터와 크기
   */
  async resizeImageIfNeeded(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const originalWidth = img.naturalWidth;
        const originalHeight = img.naturalHeight;

        console.log(`원본 이미지 크기: ${originalWidth}x${originalHeight}`);

        // 쿠팡 해상도 범위 확인 (400px x 400px ~ 8,000px x 8,000px)
        const needsResize = originalWidth < 400 || originalHeight < 400 ||
                           originalWidth > 8000 || originalHeight > 8000;

        if (!needsResize) {
          // 리사이징 불필요 - 원본 이미지를 base64로 변환
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = originalWidth;
          canvas.height = originalHeight;
          ctx.drawImage(img, 0, 0);

          const base64Data = canvas.toDataURL('image/jpeg', 0.9);
          resolve({
            imageData: base64Data,
            dimensions: { width: originalWidth, height: originalHeight }
          });
        } else {
          // 리사이징 필요 - 800x800으로 조정
          console.log(`이미지 리사이징 필요: ${originalWidth}x${originalHeight} → 800x800`);

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = 800;
          canvas.height = 800;

          // 이미지를 800x800으로 리사이징 (비율 유지하면서 중앙 정렬)
          const scale = Math.min(800 / originalWidth, 800 / originalHeight);
          const scaledWidth = originalWidth * scale;
          const scaledHeight = originalHeight * scale;
          const x = (800 - scaledWidth) / 2;
          const y = (800 - scaledHeight) / 2;

          // 흰색 배경
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, 800, 800);

          // 이미지 그리기
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

          const base64Data = canvas.toDataURL('image/jpeg', 0.9);
          resolve({
            imageData: base64Data,
            dimensions: { width: 800, height: 800 }
          });
        }
      };

      img.onerror = (err) => {
        console.error('이미지 로드 오류:', err);
        reject(err);
      };

      img.src = imageUrl;
    });
  },

  /**
   * coupangDoc에 추가 이미지 정보 부분 업데이트
   * @param {Object} product - 상품 객체
   * @param {Array} additionalImages - 추가 이미지 배열
   * @returns {Promise<boolean>} 업데이트 성공 여부
   */
  async updateProductWithCoupangDocPartial(product, additionalImages) {
    try {
      console.log('additionalImageService - coupangDoc 부분 업데이트 시작');

      // 텍스트가 제거된 이미지만 필터링
      const processedImages = additionalImages.filter(img => img.textRemoved === true);

      if (processedImages.length === 0) {
        console.log('처리된 추가 이미지가 없어 coupangDoc 업데이트 생략');
        return true;
      }

      // 이미지 URL 추출
      const imageUrls = processedImages.map(img => img.url).filter(Boolean);
      const additionalImagesJoined = imageUrls.join(',');

      // 부분 업데이트를 위한 필드 지정
      const updateFields = {
        'additionalImageStatus': 'completed',
        'additionalImages': processedImages,
        'updatedAt': Date.now()
      };

      // 상품 레벨의 coupangDoc 업데이트 (있는 경우)
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        // 모든 jsonDocument 항목에 이미지 정보 적용
        product.coupangDoc.jsonDocument.forEach((docItem, docIndex) => {
          if (docItem.imagePage) {
            // 이미지 페이지가 있는 경우
            if (!docItem.imagePage.images) {
              // 점 표기법으로 빈 객체 설정
              updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images`] = {};
            }

            // 추가 이미지 URL 업데이트 (점 표기법 사용)
            updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.additionalImage`] = additionalImagesJoined;
            console.log(`coupangDoc.jsonDocument[${docIndex}].imagePage.images.additionalImage 업데이트: ${additionalImagesJoined}`);
          }
        });
      }

      // 모든 옵션의 additionalImageStatus 업데이트
      if (product.options && product.options.length > 0) {
        product.options.forEach((option, optionIndex) => {
          // additionalImageStatus가 객체인 경우
          if (typeof option.additionalImageStatus === 'object') {
            updateFields[`options.${optionIndex}.additionalImageStatus`] = {
              ...option.additionalImageStatus,
              status: 'completed',
              images: imageUrls
            };
          }
          // additionalImageStatus가 문자열이거나 없는 경우
          else {
            updateFields[`options.${optionIndex}.additionalImageStatus`] = {
              status: 'completed',
              images: imageUrls
            };
          }
        });
      }

      // 서버에 부분 업데이트 요청
      await store.dispatch('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      console.log('additionalImageService - coupangDoc 부분 업데이트 완료');
      return true;
    } catch (error) {
      console.error('additionalImageService - coupangDoc 부분 업데이트 오류:', error);
      return false;
    }
  }
};

export default additionalImageService;